import { cn } from "@/lib/utils";

interface SpnLogoProps {
  className?: string;
  /** Visual variant. `mark` is the icon-only square; `wordmark` adds the SPN name. */
  variant?: "mark";
  title?: string;
}

/**
 * SPN brand mark — a hub-and-spoke "partner network": three outer
 * nodes orbiting a stronger central hub, joined by connection lines.
 * Built as a single SVG so it scales crisply from 16px (favicon) to
 * 256px (marketing). Color comes from the surrounding text/bg via
 * `currentColor` on the gradient stops, so it adapts to dark/light.
 */
export function SpnLogo({ className, title = "Synergistic Partner Network" }: SpnLogoProps) {
  return (
    <svg
      viewBox="0 0 64 64"
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
      className={cn("h-6 w-6", className)}
    >
      <title>{title}</title>
      <defs>
        <linearGradient id="spn-bg" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#3B82F6" />
          <stop offset="1" stopColor="#1D4ED8" />
        </linearGradient>
        <linearGradient id="spn-hub" x1="20" y1="20" x2="44" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#DBEAFE" />
        </linearGradient>
      </defs>
      {/* Rounded background tile */}
      <rect width="64" height="64" rx="14" fill="url(#spn-bg)" />
      {/* Connection lines from hub to each node */}
      <g stroke="#FFFFFF" strokeOpacity="0.55" strokeWidth="2.4" strokeLinecap="round">
        <line x1="32" y1="32" x2="32" y2="14" />
        <line x1="32" y1="32" x2="16" y2="44" />
        <line x1="32" y1="32" x2="48" y2="44" />
      </g>
      {/* Outer nodes */}
      <g fill="#FFFFFF">
        <circle cx="32" cy="14" r="5" />
        <circle cx="16" cy="44" r="5" />
        <circle cx="48" cy="44" r="5" />
      </g>
      {/* Central hub — slightly larger with subtle highlight ring */}
      <circle cx="32" cy="32" r="8.5" fill="url(#spn-hub)" />
      <circle
        cx="32"
        cy="32"
        r="8.5"
        fill="none"
        stroke="#FFFFFF"
        strokeOpacity="0.9"
        strokeWidth="1.2"
      />
    </svg>
  );
}
